import type { LatLon } from "@radar-sim/schema";
import type { AreaData, FerryTemplate, Mark } from "./types.js";
import { PLOCKTON_COASTLINE } from "./plockton-coastline.js";
import type { TidalAnomaly } from "./weymouth-portland.js";

/**
 * Plockton + Loch Carron — west-Highland teaching coverage. Sea-loch
 * and small-island radar approaches with some of the strongest tidal
 * streams in the British Isles. Coastline OSM-derived; marks, lanes,
 * ferry templates, and tidal anomalies are hand-curated placeholders.
 */

/**
 * Plockton tidal anomalies. Kyle Rhea is famous for spring streams in
 * excess of 8 kn — one of the tidal-gate hazards on the Reeds Almanac
 * "extreme caution" list. Kyle Akin under the Skye Bridge runs almost
 * as fast on a flood. Crowlin Sound and the Sound of Raasay both have
 * boat-troubling overfalls.
 */
export const PLOCKTON_TIDAL_ANOMALIES: ReadonlyArray<TidalAnomaly> = [
  {
    id: "kyle-rhea",
    name: "Kyle Rhea (extreme stream)",
    severity: 0.95,
    // Narrow strait between Skye and the mainland; springs >8 kn on
    // the south-going ebb. Borderline of our bbox.
    polygon: [
      { lat: 57.245, lon: -5.665 },
      { lat: 57.255, lon: -5.65 },
      { lat: 57.235, lon: -5.62 },
      { lat: 57.225, lon: -5.64 },
    ],
    drift: { bearingDeg: 200, speedKnots: 6 },
  },
  {
    id: "kyle-akin",
    name: "Kyle Akin (under Skye Bridge)",
    severity: 0.7,
    // Tidal stream between Kyle of Lochalsh and Kyleakin, accelerated
    // by the channel narrowing under the Skye Bridge.
    polygon: [
      { lat: 57.282, lon: -5.722 },
      { lat: 57.288, lon: -5.71 },
      { lat: 57.281, lon: -5.7 },
      { lat: 57.275, lon: -5.715 },
    ],
    drift: { bearingDeg: 90, speedKnots: 3.5 },
  },
  {
    id: "crowlin-sound",
    name: "Crowlin Sound overfalls",
    severity: 0.5,
    // Between Crowlin Islands and the mainland — strong cross-tide,
    // worst on a flood against a SW wind.
    polygon: [
      { lat: 57.36, lon: -5.85 },
      { lat: 57.37, lon: -5.83 },
      { lat: 57.355, lon: -5.81 },
      { lat: 57.345, lon: -5.83 },
    ],
    drift: { bearingDeg: 30, speedKnots: 2.5 },
  },
  {
    id: "kishorn-narrows",
    name: "Loch Kishorn narrows",
    severity: 0.45,
    // Acceleration through the narrow neck of Loch Kishorn —
    // significant ebb shoots out into the Inner Sound.
    polygon: [
      { lat: 57.405, lon: -5.69 },
      { lat: 57.415, lon: -5.68 },
      { lat: 57.41, lon: -5.66 },
      { lat: 57.4, lon: -5.67 },
    ],
    drift: { bearingDeg: 250, speedKnots: 2 },
  },
];

const MARKS: ReadonlyArray<Mark> = [
  {
    id: "eilean-bàn-light",
    name: "Eilean Bàn lighthouse",
    position: { lat: 57.282, lon: -5.722 },
    type: "light",
    rcs: 0.5,
    light: {
      character: "Iso.W.4s",
      pattern: { kind: "iso", periodSec: 4 },
      color: "W",
      heightM: 13,
      rangeNm: 8,
    },
  },
  {
    id: "kyle-akin-east-card",
    name: "Kyle Akin E cardinal",
    position: { lat: 57.279, lon: -5.708 },
    type: "buoy",
    rcs: 0.07,
    category: "east-cardinal",
  },
  {
    id: "kyle-akin-west-card",
    name: "Kyle Akin W cardinal",
    position: { lat: 57.283, lon: -5.731 },
    type: "buoy",
    rcs: 0.07,
    category: "west-cardinal",
  },
  {
    id: "plockton-bay-anchor",
    name: "Plockton Bay anchorage",
    position: { lat: 57.336, lon: -5.643 },
    type: "buoy",
    rcs: 0.06,
    category: "special",
  },
  {
    id: "kyle-of-lochalsh-entrance",
    name: "Kyle of Lochalsh entrance",
    position: { lat: 57.281, lon: -5.715 },
    type: "buoy",
    rcs: 0.08,
    category: "safe-water",
  },
  {
    id: "loch-carron-entrance",
    name: "Loch Carron entrance",
    position: { lat: 57.348, lon: -5.74 },
    type: "buoy",
    rcs: 0.06,
    category: "starboard-hand",
  },
  {
    id: "crowlin-east-light",
    name: "Crowlin Islands E light",
    position: { lat: 57.366, lon: -5.81 },
    type: "light",
    rcs: 0.4,
    light: {
      character: "Fl.W.5s",
      pattern: { kind: "flash", periodSec: 5 },
      color: "W",
      heightM: 8,
      rangeNm: 6,
    },
  },
];

const LANES: ReadonlyMap<string, ReadonlyArray<LatLon>> = new Map([
  // Plockton anchorage approach — from the Inner Sound into the
  // sheltered Plockton Bay.
  [
    "plockton-anchorage-approach",
    [
      { lat: 57.34, lon: -5.72 },
      { lat: 57.34, lon: -5.69 },
      { lat: 57.337, lon: -5.66 },
      { lat: 57.336, lon: -5.643 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Kyle of Lochalsh narrows (under the Skye Bridge) — east-bound
  // through the strait toward the open Inner Sound.
  [
    "kyle-akin-eastbound",
    [
      { lat: 57.283, lon: -5.74 },
      { lat: 57.283, lon: -5.722 },
      { lat: 57.282, lon: -5.71 },
      { lat: 57.279, lon: -5.69 },
    ] as ReadonlyArray<LatLon>,
  ],
  [
    "kyle-akin-westbound",
    [
      { lat: 57.279, lon: -5.69 },
      { lat: 57.282, lon: -5.71 },
      { lat: 57.283, lon: -5.722 },
      { lat: 57.283, lon: -5.74 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Loch Carron approach — from the Inner Sound into the loch mouth.
  [
    "loch-carron-approach",
    [
      { lat: 57.39, lon: -5.78 },
      { lat: 57.37, lon: -5.76 },
      { lat: 57.355, lon: -5.745 },
      { lat: 57.348, lon: -5.74 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Inner Sound transit — the deeper-water lane between the Crowlin
  // Islands and the Skye coast, used by passing yachts and the
  // CalMac inter-island freight runs.
  [
    "inner-sound-northbound",
    [
      { lat: 57.25, lon: -5.85 },
      { lat: 57.32, lon: -5.86 },
      { lat: 57.4, lon: -5.83 },
      { lat: 57.48, lon: -5.85 },
    ] as ReadonlyArray<LatLon>,
  ],
]);

const FERRY_TEMPLATES: ReadonlyArray<FerryTemplate> = [
  {
    id: "plockton-pilot-launch",
    name: "Plockton harbour launch",
    vesselType: "pilot",
    rcs: 0.25,
    ais: {
      mmsi: "232100301",
      name: "PLOCKTON LAUNCH",
      class: "B",
      destination: "STN.",
      callsign: "MMPL",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.336, lon: -5.643 }, cog: 270, sog: 5 },
      { atSimSeconds: 600, position: { lat: 57.34, lon: -5.69 }, cog: 270, sog: 8 },
      { atSimSeconds: 1500, position: { lat: 57.345, lon: -5.74 }, cog: 270, sog: 6 },
    ],
  },
  {
    id: "kyle-cargo-eastbound",
    name: "Kyle cargo (e-bound through Akin)",
    vesselType: "container",
    rcs: 0.7,
    ais: {
      mmsi: "232100302",
      name: "INVERESK",
      class: "A",
      destination: "INVERNESS",
      callsign: "MIVK",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.283, lon: -5.74 }, cog: 90, sog: 6 },
      { atSimSeconds: 600, position: { lat: 57.283, lon: -5.722 }, cog: 90, sog: 5 },
      { atSimSeconds: 1200, position: { lat: 57.279, lon: -5.69 }, cog: 95, sog: 8 },
    ],
  },
  {
    id: "kishorn-workboat",
    name: "Kishorn yard workboat",
    vesselType: "tug",
    rcs: 0.4,
    ais: {
      mmsi: "232100303",
      name: "KISHORN ATLAS",
      class: "B",
      destination: "WORKING",
      callsign: "MKSN",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.4, lon: -5.665 }, cog: 270, sog: 6 },
      { atSimSeconds: 600, position: { lat: 57.405, lon: -5.74 }, cog: 270, sog: 7 },
      { atSimSeconds: 1500, position: { lat: 57.41, lon: -5.84 }, cog: 270, sog: 7 },
    ],
  },
  {
    id: "calmac-inter-island",
    name: "CalMac inter-island (Hebridean Isles)",
    vesselType: "ferry",
    rcs: 0.85,
    ais: {
      mmsi: "232100304",
      name: "HEBRIDEAN ISLES",
      class: "A",
      destination: "RAASAY",
      callsign: "MCMA",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.25, lon: -5.85 }, cog: 0, sog: 12 },
      { atSimSeconds: 900, position: { lat: 57.32, lon: -5.86 }, cog: 0, sog: 13 },
      { atSimSeconds: 1800, position: { lat: 57.4, lon: -5.83 }, cog: 5, sog: 13 },
      { atSimSeconds: 2700, position: { lat: 57.48, lon: -5.85 }, cog: 0, sog: 12 },
    ],
  },
];

export const PLOCKTON: AreaData = {
  area: "plockton",
  boundingBox: {
    nw: { lat: 57.55, lon: -5.95 },
    se: { lat: 57.18, lon: -5.3 },
  },
  coastline: PLOCKTON_COASTLINE,
  depthContours: [],
  marks: MARKS,
  lanes: LANES,
  raconIds: new Set(),
  ferryTemplates: FERRY_TEMPLATES,
};
